import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ArrowBack as ArrowBackIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { Organization, OrganizationType } from '../../models/Organization';
import {
  OrganizationSubscriptionStatus,
  SubscriptionStatus,
} from '../../models/Subscription';
import SubscriptionPlans from '../../components/SubscriptionPlans';
import {
  clearSubscriptionAwaitingStripe,
  hasSubscriptionWebhookCaughtUp,
  notifyOrganizationTypeUpdated,
  readSubscriptionAwaitingStripe,
  STRIPE_RETURN_POLL_INTERVAL_MS,
  STRIPE_RETURN_POLL_TIMEOUT_MS,
} from '../../utils/subscriptionStripeReturn';

const OPEN_SUBSCRIPTION_STATUSES = new Set<SubscriptionStatus>([
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.PAST_DUE,
  SubscriptionStatus.PAUSED,
]);

/**
 * Full-page plan catalogue for upgrades / downgrades / org-type switches.
 * Opened from Plan and Billing via “Switch plan”.
 */
const ChangeSubscriptionPlanPage: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingAfterStripe, setSyncingAfterStripe] = useState(
    () => Boolean(readSubscriptionAwaitingStripe()),
  );
  const [stripeSyncTimedOut, setStripeSyncTimedOut] = useState(false);
  /** Bump to restart webhook polling after an in-page private-offer `/change`. */
  const [stripeSyncGeneration, setStripeSyncGeneration] = useState(0);
  const lastOrgTypeRef = useRef<OrganizationType | null | undefined>(undefined);

  const applyBillingState = useCallback((org: Organization, status: OrganizationSubscriptionStatus) => {
    setOrganization(org);
    setSubscription(status);
    const previous = lastOrgTypeRef.current;
    const next = org.organizationType ?? null;
    lastOrgTypeRef.current = next;
    if (previous !== undefined && next && previous !== next) {
      notifyOrganizationTypeUpdated(next);
    }
  }, []);

  const refreshBillingState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [org, status] = await Promise.all([
        hostApiClient.getOrganization(),
        hostApiClient.getSubscription(),
      ]);
      applyBillingState(org, status);
      return { org, status };
    } catch (err) {
      setError(t('subscription.fetchError', 'Failed to fetch organization subscription'));
      console.error('Error fetching subscription state:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyBillingState, t]);

  useEffect(() => {
    void refreshBillingState();
  }, [refreshBillingState]);

  useEffect(() => {
    const baseline = readSubscriptionAwaitingStripe();
    if (!baseline) {
      setSyncingAfterStripe(false);
      return;
    }

    setSyncingAfterStripe(true);
    setStripeSyncTimedOut(false);
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const finish = (timedOut: boolean) => {
      clearSubscriptionAwaitingStripe();
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
      if (!cancelled) {
        setSyncingAfterStripe(false);
        if (timedOut) {
          setStripeSyncTimedOut(true);
        } else {
          navigate('/host/subscription', { replace: true });
        }
      }
    };

    const pollOnce = async () => {
      if (Date.now() - baseline.startedAt > STRIPE_RETURN_POLL_TIMEOUT_MS) {
        finish(true);
        return;
      }
      try {
        const [org, status] = await Promise.all([
          hostApiClient.getOrganization(),
          hostApiClient.getSubscription(),
        ]);
        if (cancelled) return;
        applyBillingState(org, status);
        if (
          hasSubscriptionWebhookCaughtUp(baseline, {
            planCode: status.planCode,
            status: status.status,
            organizationType: org.organizationType,
          })
        ) {
          finish(false);
        }
      } catch (err) {
        console.error('Error polling subscription after Stripe return:', err);
      }
    };

    void pollOnce();
    intervalId = setInterval(() => {
      void pollOnce();
    }, STRIPE_RETURN_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [applyBillingState, navigate, stripeSyncGeneration]);

  const status = subscription?.status ?? null;
  const planCode = subscription?.planCode ?? null;
  const canChangePlan =
    Boolean(status && planCode) && status != null && OPEN_SUBSCRIPTION_STATUSES.has(status);

  if (loading && !subscription) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !subscription) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!canChangePlan) {
    return <Navigate to="/host/subscription" replace />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 8, md: 10 },
        maxWidth: 960,
        width: '100%',
        mx: 'auto',
        minHeight: 'calc(100vh - 140px)',
      }}
    >
      <Stack spacing={1} sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.25,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.16),
              border: 1,
              borderColor: alpha(theme.palette.primary.main, 0.28),
            }}
          >
            <SwapHorizIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            {t('subscription.changeTitle', 'Upgrade or change plan')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          {t(
            'subscription.changeSubtitle',
            'Compare monthly plans and commissions. Public plans continue in Stripe; special offers apply directly. Your current plan stays active until the change completes.',
          )}
        </Typography>
      </Stack>

      {syncingAfterStripe && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 2.5, borderRadius: 2 }}
          onClose={() => {
            clearSubscriptionAwaitingStripe();
            setSyncingAfterStripe(false);
          }}
        >
          {t(
            'subscription.syncingAfterStripe',
            'Checking whether Stripe confirmed your plan change…',
          )}
        </Alert>
      )}

      {stripeSyncTimedOut && !syncingAfterStripe && (
        <Alert
          severity="warning"
          sx={{ mb: 2.5, borderRadius: 2 }}
          onClose={() => setStripeSyncTimedOut(false)}
        >
          {t(
            'subscription.stripeSyncTimedOut',
            'No plan change showed up yet. If you confirmed in Stripe, wait a moment and refresh — local webhooks may still be catching up. If you cancelled, your current plan is unchanged.',
          )}
        </Alert>
      )}

      <SubscriptionPlans
        mode="change"
        hideHeader
        organizationType={organization?.organizationType}
        currentPlanCode={planCode}
        currentStatus={status}
        onSubscriptionChange={() => {
          if (readSubscriptionAwaitingStripe()) {
            setStripeSyncTimedOut(false);
            setStripeSyncGeneration((generation) => generation + 1);
          }
          void refreshBillingState();
        }}
      />

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/host/subscription')}
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            ml: -0.5,
          }}
        >
          {t('common.back', 'Back')}
        </Button>
      </Box>
    </Box>
  );
};

export default ChangeSubscriptionPlanPage;
